import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CryptoJS from "crypto-js";
import InputBox from "../../components/InputBox";
import Checkbox from "../../components/Checkbox";
import Button from "../../components/Button";
import DropdownBox from "../../components/DropdownBox";
import MultiSelectDropdown from "../../components/MultiSelectDropdown";
import { clearAuthSession, getMetadata, updateMetadata } from "../../utils/encryptedStorage";
import { useTimezone } from "../../contexts/TimezoneContext";
import { RESUME_LIBRARY_LABEL } from "../../utils/uxLabels";

const SELECT_OPTIONS = {
  "Industry": [
    "Technology & Software",
    "Healthcare & Life Sciences",
    "Financial Services & Banking",
    "E-commerce & Retail",
    "Manufacturing & Industrial",
    "Education & Training",
    "Consulting & Professional Services",
    "Media & Entertainment",
    "Telecommunications",
    "Real Estate & Construction"
  ],
  "Company size": [
    "1-10 employees",
    "11-50 employees",
    "51-200 employees",
    "201-500 employees",
    "501-1000 employees",
    "1001-5000 employees",
    "5000+ employees"
  ],
  "Primary Hiring Locations": [
    "United States",
    "India",
    "United Kingdom",
    "Canada",
    "Germany",
    "Australia",
    "Singapore",
    "UAE",
    "Netherlands",
    "Remote - Global"
  ],
  "Timezone preference (optional)": [
    "UTC-8 (PST)",
    "UTC-5 (EST)",
    "UTC+0 (GMT/UTC)",
    "UTC+1 (CET)",
    "UTC+5:30 (IST)",
    "UTC+8 (SGT)",
    "UTC+10 (AEST)",
    "Flexible"
  ],
  "Primary Sourcing Platforms": [
    "LinkedIn",
    "Online Sourcing",
    "Resume Library",
    "Naukri.com",
    "Indeed",
    "GitHub",
    "Stack Overflow",
    "AngelList",
    "Glassdoor",
    "Monster",
    "CareerBuilder",
    "ZipRecruiter"
  ],
  "Candidate Discovery Type": [
    "Active job seekers",
    "Passive candidates",
    "Referrals",
    "Campus hiring",
    "Internal mobility",
    "Mixed approach"
  ],
  "Automation Level": [
    "Fully manual",
    "Semi-automated",
    "Highly automated",
    "AI-assisted",
    "Fully automated"
  ],
  "Follow-up Cadence": [
    "1 day",
    "3 days",
    "5 days",
    "1 week",
    "2 weeks",
    "Custom"
  ]
};

// Multi-select options for primary sourcing platforms
const PRIMARY_SOURCING_PLATFORMS_OPTIONS = [
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'google', label: 'Online Sourcing' },
  { value: 'resumebox', label: RESUME_LIBRARY_LABEL },
  { value: 'naukri.com', label: 'Naukri.com' },
  { value: 'indeed', label: 'Indeed' },
  { value: 'github', label: 'GitHub' },
  { value: 'stackoverflow', label: 'Stack Overflow' },
  { value: 'angellist', label: 'AngelList' },
  { value: 'glassdoor', label: 'Glassdoor' },
  { value: 'monster', label: 'Monster' },
  { value: 'careerbuilder', label: 'CareerBuilder' },
  { value: 'ziprecruiter', label: 'ZipRecruiter' }
];

// Multi-select options for outreach channels
const OUTREACH_CHANNEL_OPTIONS = [
  { value: 'gmail', label: 'Mail' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'linkedin', label: 'LinkedIn Mail' }
];

// Multi-select options for candidate discovery types
const CANDIDATE_DISCOVERY_TYPE_OPTIONS = [
  { value: 'active_job_seekers', label: 'Active job seekers' },
  { value: 'passive_candidates', label: 'Passive candidates' },
  { value: 'referrals', label: 'Referrals' },
  { value: 'campus_hiring', label: 'Campus hiring' },
  { value: 'internal_mobility', label: 'Internal mobility' },
  { value: 'mixed_approach', label: 'Mixed approach' }
];

const BusinessDetailsPage = ({ mode: propMode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshTimezone } = useTimezone();
  
  // Determine mode: 'create' for new workspace setup, 'update' for editing existing company
  // Can be passed as prop or determined from URL path
  const isCreateMode = propMode === 'create' || location.pathname.includes('workspace-setup');
  const isUpdateMode = !isCreateMode;
  
  // State for form data
  const [formData, setFormData] = useState({
    com_id: null,
    company_name: "",
    website_url: "",
    industry: "",
    company_size: "",
    primary_hiring_locations: "",
    timezone_preference: "",
    primary_sourcing_platforms: "",
    candidate_discovery_type: "",
    linkedin_company_page_url: "",
    naukri_employer_profile_url: "",
    careers_page_url: "",
    other_job_boards: "",
    outreach_channels: "",
    automation_level: "",
    follow_up_cadence: "",
    restrictions: "",
  });
  
  // Multi-select state for primary sourcing platforms and outreach channels
  const [selectedPrimarySourcingPlatforms, setSelectedPrimarySourcingPlatforms] = useState([]);
  const [selectedOutreachChannels, setSelectedOutreachChannels] = useState([]);
  const [selectedCandidateDiscoveryTypes, setSelectedCandidateDiscoveryTypes] = useState([]);

  // State for checkboxes
  const [hiringGoals, setHiringGoals] = useState({
    "Fill current openings": false,
    "Build pipeline": false,
    "Confidential hiring": false,
    "Leadership hiring": false,
    "High-volume roles": false,
  });
  const [locationTypes, setLocationTypes] = useState({
    Remote: false,
    Hybrid: false,
    "On-site": false,
  });
  
  const [showMaxSelectionWarning, setShowMaxSelectionWarning] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Fetch company data on mount (only in update mode)
  useEffect(() => {
    const fetchCompanyData = async () => {
      // In create mode, skip fetching and just set loading to false
      if (isCreateMode) {
        setIsLoading(false);
        return;
      }
      
      try {
        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
        const SECRET_KEY = process.env.REACT_APP_SECRETKEY;
        
        // Get user ID from localStorage or encrypted metadata
        let userId = null;
        try {
          const metadata = getMetadata();
          if (metadata) {
            userId = metadata.uid || metadata.id || metadata.user_id;
          }
          if (!userId) {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            userId = user.uid || 1;
          }
        } catch (err) {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          userId = user.uid || 1;
        }

        // Encrypt the created_by parameter
        const payload = { created_by: userId };
        const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();

        // Send as POST with encrypted body
        const response = await fetch(`${API_URL}/company/get`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: encrypted })
        });
        const data = await response.json();

        if (response.ok && data.data) {
          // Decrypt the response
          const bytes = CryptoJS.AES.decrypt(data.data, SECRET_KEY);
          const decrypted = bytes.toString(CryptoJS.enc.Utf8);
          const responseData = JSON.parse(decrypted);
          
          const { company, hiring_preferences, job_distribution_channels, other_preferences } = responseData;
          // Populate form data
          setFormData({
            com_id: company.id,
            company_name: company.company_name || "",
            website_url: company.website_url || "",
            industry: company.industry || "",
            company_size: company.company_size || "",
            primary_hiring_locations: hiring_preferences?.primary_hiring_locations || "",
            timezone_preference: hiring_preferences?.timezone_preference || "",
            primary_sourcing_platforms: hiring_preferences?.primary_sourcing_platforms || "",
            candidate_discovery_type: hiring_preferences?.candidate_discovery_type || "",
            linkedin_company_page_url: job_distribution_channels?.linkedin_company_page_url || "",
            naukri_employer_profile_url: job_distribution_channels?.naukri_employer_profile_url || "",
            careers_page_url: job_distribution_channels?.careers_page_url || "",
            other_job_boards: job_distribution_channels?.other_job_boards || "",
            outreach_channels: other_preferences?.outreach_channels || "",
            automation_level: other_preferences?.automation_level || "",
            follow_up_cadence: other_preferences?.follow_up_cadence || "",
            restrictions: other_preferences?.restrictions || "",
          });
          
          // Parse and set primary sourcing platforms from comma-separated string
          if (hiring_preferences?.primary_sourcing_platforms) {
            const platforms = hiring_preferences.primary_sourcing_platforms.split(',').map(p => p.trim().toLowerCase());
            setSelectedPrimarySourcingPlatforms(platforms.filter(p => PRIMARY_SOURCING_PLATFORMS_OPTIONS.some(opt => opt.value === p)));
          }
          
          // Parse and set outreach channels from comma-separated string
          if (other_preferences?.outreach_channels) {
            const channels = other_preferences.outreach_channels.split(',').map(c => c.trim().toLowerCase());
            setSelectedOutreachChannels(channels.filter(c => OUTREACH_CHANNEL_OPTIONS.some(opt => opt.value === c)));
          }
          
          // Parse and set candidate discovery types from comma-separated string
          if (hiring_preferences?.candidate_discovery_type) {
            const types = hiring_preferences.candidate_discovery_type.split(',').map(t => t.trim().toLowerCase().replace(/\s+/g, '_'));
            setSelectedCandidateDiscoveryTypes(types.filter(t => CANDIDATE_DISCOVERY_TYPE_OPTIONS.some(opt => opt.value === t)));
          }

          // Populate hiring goals
          if (hiring_preferences) {
            setHiringGoals({
              "Fill current openings": hiring_preferences.fill_current_openings || false,
              "Build pipeline": hiring_preferences.build_pipeline || false,
              "Confidential hiring": hiring_preferences.confidential_hiring || false,
              "Leadership hiring": hiring_preferences.leadership_hiring || false,
              "High-volume roles": hiring_preferences.high_volume_roles || false,
            });
          }

          // Populate location types from location_type string
          if (hiring_preferences?.location_type) {
            const locationArr = hiring_preferences.location_type.split(", ");
            setLocationTypes({
              Remote: locationArr.includes("Remote"),
              Hybrid: locationArr.includes("Hybrid"),
              "On-site": locationArr.includes("On-site"),
            });
          }
        } else if (isUpdateMode) {
          // In update mode but no company found - redirect to create
          navigate('/auth/workspace-setup');
          return;
        }
      } catch (err) {
        console.error('Failed to fetch company data:', err);
        setErrors({ submit: 'Failed to load company details' });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanyData();
  }, []);

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleHiringGoalChange = (label) => {
    setHiringGoals((prev) => {
      const currentSelected = Object.values(prev).filter(Boolean).length;
      
      // If trying to select and already have 4 selected
      if (!prev[label] && currentSelected >= 4) {
        setShowMaxSelectionWarning(true);
        setTimeout(() => setShowMaxSelectionWarning(false), 3000);
        return prev;
      }
      
      return { ...prev, [label]: !prev[label] };
    });
  };
  
  const handleLocationTypeChange = (label) => {
    setLocationTypes((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required field
    if (!formData.company_name || !formData.company_name.trim()) {
      setErrors({ company_name: 'Company name is required' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    try {
      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
      const SECRET_KEY = process.env.REACT_APP_SECRETKEY;
      
      if (!SECRET_KEY) {
        setErrors({ submit: 'Missing REACT_APP_SECRETKEY configuration' });
        return;
      }
      
      // Get user ID for created_by
      let userId = null;
      try {
        const metadata = getMetadata();
        if (metadata) {
          userId = metadata.uid || metadata.id || metadata.user_id;
        }
        if (!userId) {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          userId = user.uid || user.id || 1;
        }
      } catch (err) {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        userId = user.uid || user.id || 1;
      }

      // Prepare location_type string from checkboxes
      const selectedLocations = Object.keys(locationTypes).filter(key => locationTypes[key]);
      const location_type = selectedLocations.join(', ');
      
      // Prepare primary sourcing platforms as comma-separated string
      const primarySourcingPlatformsStr = selectedPrimarySourcingPlatforms.join(',');
      
      // Prepare outreach channels as comma-separated string
      const outreachChannelsStr = selectedOutreachChannels.join(',');
      
      // Prepare candidate discovery types as comma-separated string
      const candidateDiscoveryTypesStr = selectedCandidateDiscoveryTypes.join(',');

      // Build payload
      const payload = {
        company_name: formData.company_name,
        website_url: formData.website_url,
        industry: formData.industry,
        company_size: formData.company_size,
        created_by: parseInt(userId, 10),
        // Hiring preferences (nested object)
        hiring_preferences: {
          fill_current_openings: hiringGoals["Fill current openings"],
          build_pipeline: hiringGoals["Build pipeline"],
          confidential_hiring: hiringGoals["Confidential hiring"],
          leadership_hiring: hiringGoals["Leadership hiring"],
          high_volume_roles: hiringGoals["High-volume roles"],
          location_type,
          primary_hiring_locations: formData.primary_hiring_locations,
          timezone_preference: formData.timezone_preference,
          primary_sourcing_platforms: primarySourcingPlatformsStr,
          candidate_discovery_type: candidateDiscoveryTypesStr,
        },
        // Job distribution channels (nested object)
        job_distribution_channels: {
          linkedin_company_page_url: formData.linkedin_company_page_url,
          naukri_employer_profile_url: formData.naukri_employer_profile_url,
          careers_page_url: formData.careers_page_url,
          other_job_boards: formData.other_job_boards,
        },
        // Other preferences (nested object)
        other_preferences: {
          outreach_channels: outreachChannelsStr,
          automation_level: formData.automation_level,
          follow_up_cadence: formData.follow_up_cadence,
          restrictions: formData.restrictions,
        },
      };
      
      // For update mode, include com_id
      if (isUpdateMode && formData.com_id) {
        payload.com_id = formData.com_id;
      }

      // Encrypt the payload
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();

      // Send to backend - different endpoint for create vs update
      const endpoint = isCreateMode ? `${API_URL}/company/create` : `${API_URL}/company/update`;
      const method = isCreateMode ? 'POST' : 'PUT';
      
      const response = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: encrypted })
      });

      const data = await response.json();

      if (response.ok) {
        // For create mode, update user's cid in localStorage and redirect
        if (isCreateMode && data.data) {
          try {
            const bytes = CryptoJS.AES.decrypt(data.data, SECRET_KEY);
            const decrypted = bytes.toString(CryptoJS.enc.Utf8);
            const responseData = JSON.parse(decrypted);
            
            if (responseData.com_id) {
              // Update metadata with new company ID
              updateMetadata({ cid: responseData.com_id });
            }
          } catch (parseErr) {
            console.error('Failed to parse create response:', parseErr);
          }

          // Show success and redirect
          setShowSuccessModal(true);
          setTimeout(() => {
            setShowSuccessModal(false);
            navigate('/auth/credentials-setup');
          }, 2000);
        } else {
          // Update mode - just show success, refresh timezone across the app
          refreshTimezone();
          setShowSuccessModal(true);
          setTimeout(() => setShowSuccessModal(false), 3000);
        }
      } else {
        // Handle backend validation errors
        const errorMessage = data.error || data.message || 'Failed to update company details';
        
        // Map error message to specific field based on content
        if (errorMessage.toLowerCase().includes('company name')) {
          setErrors({ company_name: errorMessage });
        } else if (errorMessage.toLowerCase().includes('company id')) {
          setErrors({ submit: errorMessage });
        } else if (errorMessage.toLowerCase().includes('industry')) {
          setErrors({ industry: errorMessage });
        } else if (errorMessage.toLowerCase().includes('company size')) {
          setErrors({ company_size: errorMessage });
        } else if (data.errors && Array.isArray(data.errors)) {
          // If backend sends full error array, use it
          const errorMap = {};
          data.errors.forEach(err => {
            errorMap[err.param || 'submit'] = err.msg;
          });
          setErrors(errorMap);
        } else {
          setErrors({ submit: errorMessage });
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      console.error('Failed to update company:', err);
      setErrors({ submit: 'Failed to update company details' });
    }
  };

  const handleSignOut = () => {
    clearAuthSession();
    navigate('/auth/login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex bg-[#F6F7F9] dark:bg-gray-900 items-center justify-center">
        <p className="text-gray-600 dark:text-gray-400">Loading company details...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex bg-[#F6F7F9] dark:bg-gray-900">
      {/* Main Content */}
      <main className="flex-1 min-h-screen bg-white dark:bg-gray-900 px-10 p-[2.5rem] pb-4 transition-colors duration-250">
        <div className="w-full">
          {isCreateMode && (
            <div className="mb-6 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSignOut}
                className="gap-2 rounded-full border-slate-200 px-4 py-2 text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H9m4 8H7a2 2 0 01-2-2V6a2 2 0 012-2h6" />
                </svg>
                Sign Out
              </Button>
            </div>
          )}
          <form className="space-y-10" onSubmit={handleSubmit}>
            {/* Company Basics */}
            <section>
              <h2 className="text-base font-semibold text-slate-800 dark:text-white mb-5">Company Basics</h2>
              <div className="grid grid-cols-4 gap-5">
                <InputBox 
                  label="Company name" 
                  placeholder="Samsung" 
                  value={formData.company_name}
                  onChange={(e) => handleInputChange("company_name", e.target.value)}
                  error={errors.company_name}
                />
                <InputBox 
                  label="Website URL" 
                  placeholder="https://" 
                  value={formData.website_url}
                  onChange={(e) => handleInputChange("website_url", e.target.value)}
                  error={errors.website_url}
                />
                <DropdownBox 
                  label="Industry" 
                  placeholder="Select Industry"
                  value={formData.industry}
                  options={SELECT_OPTIONS["Industry"]}
                  onChange={value => handleInputChange("industry", value)}
                  error={errors.industry}
                />
                <DropdownBox 
                  label="Company size" 
                  placeholder="Select Company Size"
                  value={formData.company_size}
                  options={SELECT_OPTIONS["Company size"]}
                  onChange={value => handleInputChange("company_size", value)}
                  error={errors.company_size}
                />
              </div>
            </section>
            {/* Hiring Preferences */}
            <section>
              <h2 className="text-base font-semibold text-slate-800 dark:text-white mb-5">Hiring Preferences</h2>
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 mb-6">
                {/* Primary Hiring Goal — flexible */}
                <div>
                  <p className="text-sm font-medium text-slate-600 dark:text-gray-200 mb-2">
                    Primary Hiring Goal <span className="italic text-slate-400 font-normal">(Select max 4)</span>
                  </p>
                  <div className="flex flex-wrap gap-6 w-fit">
                    {Object.keys(hiringGoals).map((label) => (
                      <Checkbox
                        key={label}
                        label={label}
                        checked={hiringGoals[label]}
                        onChange={() => handleHiringGoalChange(label)}
                      />
                    ))}
                  </div>
                </div>
                {/* Location Type — auto width */}
                <div>
                  <div className="grid grid-cols-[2px_auto] mb-2">
                    <div />
                    <p className="text-sm font-medium text-slate-600 dark:text-gray-200">Location Type</p>
                  </div>
                  <div className="flex flex-wrap gap-6 w-fit">
                    {Object.keys(locationTypes).map((label) => (
                      <Checkbox
                        key={label}
                        label={label}
                        checked={locationTypes[label]}
                        onChange={() => handleLocationTypeChange(label)}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <DropdownBox
                  label="Primary Hiring Locations"
                  placeholder="Select Primary Hiring Locations"
                  value={formData.primary_hiring_locations}
                  options={SELECT_OPTIONS["Primary Hiring Locations"]}
                  onChange={value => handleInputChange("primary_hiring_locations", value)}
                />
                <DropdownBox
                  label="Timezone preference (optional)"
                  placeholder="Select Timezone"
                  value={formData.timezone_preference}
                  options={SELECT_OPTIONS["Timezone preference (optional)"]}
                  onChange={value => handleInputChange("timezone_preference", value)}
                />
                <MultiSelectDropdown
                  label="Primary Sourcing Platforms"
                  placeholder="Select platforms..."
                  options={PRIMARY_SOURCING_PLATFORMS_OPTIONS}
                  selectedValues={selectedPrimarySourcingPlatforms}
                  onChange={setSelectedPrimarySourcingPlatforms}
                />
                <MultiSelectDropdown
                  label="Candidate Discovery Type"
                  placeholder="Select discovery types..."
                  options={CANDIDATE_DISCOVERY_TYPE_OPTIONS}
                  selectedValues={selectedCandidateDiscoveryTypes}
                  onChange={setSelectedCandidateDiscoveryTypes}
                />
              </div>
            </section>
            {/* Job Distribution Channels */}
            <section>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-base font-semibold text-slate-800 dark:text-white">Job Distribution Channels</h2>
                {/* Add More button hidden per requirement */}
              </div>
              <div className="grid grid-cols-4 gap-5">
                <InputBox
                  label="LinkedIn Company Page URL"
                  placeholder="https://"
                  value={formData.linkedin_company_page_url}
                  onChange={(e) => handleInputChange("linkedin_company_page_url", e.target.value)}
                />
                <InputBox
                  label="Naukri Employer Profile URL"
                  placeholder="https://"
                  value={formData.naukri_employer_profile_url}
                  onChange={(e) => handleInputChange("naukri_employer_profile_url", e.target.value)}
                />
                <InputBox
                  label="Careers Page URL"
                  placeholder="https://"
                  value={formData.careers_page_url}
                  onChange={(e) => handleInputChange("careers_page_url", e.target.value)}
                />
                <InputBox
                  label="Other job boards"
                  placeholder="https://"
                  value={formData.other_job_boards}
                  onChange={(e) => handleInputChange("other_job_boards", e.target.value)}
                />
              </div>
            </section>
            {/* Other Preferences */}
            <section>
              <h2 className="text-base font-semibold text-slate-800 dark:text-white mb-5">Other Preferences</h2>
              
              {/* All Other Preferences fields in a single 4-column row */}
              <div className="grid grid-cols-4 gap-5">
                <MultiSelectDropdown
                  label="Outreach Channels"
                  placeholder="Select outreach channels..."
                  options={OUTREACH_CHANNEL_OPTIONS}
                  selectedValues={selectedOutreachChannels}
                  onChange={setSelectedOutreachChannels}
                  dropUpRight={true}
                />
                <DropdownBox
                  label="Automation Level"
                  placeholder="Select Automation Level"
                  value={formData.automation_level}
                  options={SELECT_OPTIONS["Automation Level"]}
                  onChange={value => handleInputChange("automation_level", value)}
                  dropUpRight={true}
                />
                <DropdownBox
                  label="Follow-up Cadence"
                  placeholder="Select Follow-up Cadence"
                  value={formData.follow_up_cadence}
                  options={SELECT_OPTIONS["Follow-up Cadence"]}
                  onChange={value => handleInputChange("follow_up_cadence", value)}
                  dropUpRight={true}
                />
                <InputBox
                  label="Restrictions (if any)"
                  placeholder="Enter Restrictions"
                  value={formData.restrictions}
                  onChange={(e) => handleInputChange("restrictions", e.target.value)}
                />
              </div>
            </section>
            {/* Error Message */}
            {errors.submit && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg">
                {errors.submit}
              </div>
            )}
            {/* Actions */}
            <div className="flex justify-end gap-4 pt-8">
              <Button type="submit" variant="primary">
                {isCreateMode ? 'Create Workspace' : 'Update Changes'}
              </Button>
            </div>
          </form>
        </div>
      </main>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md mx-4 shadow-2xl transform transition-all">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
                <svg className="h-8 w-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Success!
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {isCreateMode 
                  ? 'Workspace created successfully! Redirecting...' 
                  : 'Company details updated successfully!'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SelectField = ({ label, placeholder, value, onChange, error }) => {
  const options = SELECT_OPTIONS[label] || [];

  return (
    <div>
      <label className="block text-sm font-bold text-slate-700 dark:text-gray-300 mb-2 transition-colors duration-250">{label}</label>
      <select
        className={`
          w-full h-12 px-4 py-2.5 rounded-lg border-2
          bg-gray-50 dark:bg-gray-800
          text-gray-900 dark:text-gray-100
          placeholder-gray-400 dark:placeholder-gray-500
          text-sm
          transition-[background-color,border-color,color] duration-250 ease-out
          focus:outline-none focus:ring-0
          ${error 
            ? 'border-red-500 dark:border-red-500' 
            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
          }
        `}
        value={value || ""}
        onChange={(e) => onChange && onChange(e.target.value)}
      >
        <option value="">{placeholder}</option>
        {options.map((option, index) => (
          <option key={index} value={option}>
            {option}
          </option>
        ))}
      </select>
      {error && (
        <p className="mt-1.5 text-sm text-red-500 transition-colors duration-250">{error}</p>
      )}
    </div>
  );
};

export default BusinessDetailsPage;
